'use client';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { use, useEffect, useState } from 'react';
import type { IconType } from 'react-icons';
import { BsFillTrophyFill } from 'react-icons/bs';
import { TbArrowUpRight, TbCoinFilled, TbStarFilled } from 'react-icons/tb';
import { StoryObject } from '@benzinga/basic-news-manager';
import { EdgeBadge } from '@benzinga/core-ui';
import { EdgeManager, type PortfolioOverview } from '@benzinga/edge-manager';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { SessionContext } from '@benzinga/session-context';
import { PortfolioSectionHeader } from '../PortfolioSectionHeader';
import { EdgeIdeaCardSkeleton, EdgePortfolioCardSkeleton } from '../WatchlistInsightsSkeleton';

dayjs.extend(relativeTime);

const portfolioCopy: Record<string, string> = {
  'perfect-stock-portfolio':
    'The best stocks to own, as identified by Tim Melvin\'s criteria honed over 30+ years and based on Ben Graham\'s famous 1949 "The Intelligent Investor."',
  'easy-income-portfolio':
    'A portfolio set to benefit from every major trend shaping our future, delivering regular and consistent income to your account.',
};

const portfolioDefinitions = [
  {
    slug: 'perfect-stock-portfolio',
    title: 'Perfect Stock Portfolio',
    url: 'https://www.benzinga.com/portfolio/perfect-stock-portfolio',
  },
  {
    slug: 'easy-income-portfolio',
    title: 'Easy Income Portfolio',
    url: 'https://www.benzinga.com/portfolio/easy-income-portfolio',
  },
] as const;

const portfolioIcons: Record<string, IconType> = {
  'perfect-stock-portfolio': TbStarFilled,
  'easy-income-portfolio': TbCoinFilled,
};

const ideaTypeLabels = [
  'Stock of the day',
  'Options trades',
  'Insider report',
  'Under the radar',
  'Whisper index',
] as const;

const STOCK_OF_THE_DAY_TAG = 931449;
const IDEA_DISPLAY_LIMIT = 3;
const EDGE_DASHBOARD_URL = 'https://www.benzinga.com/edge/dashboard';

const EdgeIdeasEmptyState = () => (
  <div className="col-span-full rounded-xl border border-bzgray-800 bg-[#192940] p-4 sm:p-5">
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="min-w-0">
        <p className="text-sm font-bold text-white">Nothing published in this feed yet</p>
        <p className="mt-1 max-w-xl text-sm leading-relaxed text-bzgray-400">
          Edge analysts drop stock picks, options setups, and catalyst notes throughout the session. Recent ideas show up here first.
        </p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {ideaTypeLabels.map(label => (
            <span
              key={label}
              className="rounded-md bg-white/5 px-2 py-0.5 text-[11px] font-semibold text-slate-300 ring-1 ring-white/10"
            >
              {label}
            </span>
          ))}
        </div>
      </div>
      <a
        href={EDGE_DASHBOARD_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex shrink-0 items-center justify-center gap-1.5 self-start rounded-lg bg-blue-500/15 px-4 py-2.5 text-sm font-bold text-blue-400 ring-1 ring-blue-500/25 transition-[background-color,color] duration-150 hover:bg-blue-500/25 hover:text-blue-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 lg:self-center"
      >
        Browse Edge dashboard
        <TbArrowUpRight className="h-4 w-4" aria-hidden />
      </a>
    </div>
  </div>
);

export const EdgeSection = () => {
  const session = use(SessionContext);
  const [ideas, setIdeas] = useState<StoryObject[]>([]);
  const [portfolios, setPortfolios] = useState<PortfolioOverview[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchEdgeContent = async () => {
      try {
        const edgeContent = await session.getManager(EdgeManager).getEdgeContent();
        if (!isMounted) return;
        setIdeas(edgeContent.ok?.ideas ?? []);
        setPortfolios(edgeContent.ok?.portfolios ?? []);
      } catch (error) {
        console.error('Error getting edge content:', error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchEdgeContent();
    return () => {
      isMounted = false;
    };
  }, [session]);

  const displayedIdeas = ideas.slice(0, IDEA_DISPLAY_LIMIT);
  const portfolioBySlug = Object.fromEntries(portfolios.map(portfolio => [portfolio.slug, portfolio]));

  return (
    <section className="overflow-hidden rounded-xl border border-bzgray-800 bg-[#122135] shadow-[0_1px_2px_rgba(15,23,42,0.2)]">
      <PortfolioSectionHeader
        title="Trade Ideas & Portfolios"
        titleAddon={<span className="origin-left scale-90">{EdgeBadge}</span>}
        subtitle="Model portfolios and the latest trade ideas from Edge analysts"
        tone="edge"
      />

      <div className="flex flex-col gap-5 p-5 sm:gap-6 sm:p-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
          {isLoading ? (
            Array.from({ length: 2 }).map((_, index) => <EdgePortfolioCardSkeleton key={index} />)
          ) : (
            portfolioDefinitions.map(definition => {
              const portfolio = portfolioBySlug[definition.slug];
              const Icon = portfolioIcons[definition.slug] ?? TbStarFilled;
              const description = portfolioCopy[definition.slug];
              const href = portfolio?.url ?? definition.url;

              return (
                <a
                  key={definition.slug}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col gap-4 rounded-xl border border-bzgray-800 bg-[#192940] p-4 no-underline transition-[border-color,background-color] duration-150 ease-out hover:border-blue-500/40 hover:bg-[#1e2d43] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 sm:p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500 text-white shadow-sm">
                        <Icon className="h-5 w-5" aria-hidden />
                      </span>
                      <div className="min-w-0">
                        <h3 className="m-0 text-[15px] font-bold leading-snug text-balance text-white transition-colors duration-150 group-hover:text-blue-400">
                          {portfolio?.title ?? definition.title}
                        </h3>
                        {description ? (
                          <p className="m-0 mt-1.5 line-clamp-2 text-sm leading-relaxed text-pretty text-bzgray-400">
                            {description}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    {portfolio ? (
                      <span className="shrink-0 rounded-md bg-white/5 px-2 py-0.5 text-xs font-bold tabular-nums text-slate-200">
                        {portfolio.tickers.length} holdings
                      </span>
                    ) : (
                      <span className="shrink-0 rounded-md bg-amber-500/10 px-2 py-0.5 text-xs font-bold text-amber-300/90 ring-1 ring-amber-500/20">
                        Snapshot pending
                      </span>
                    )}
                  </div>

                  <div className="mt-auto flex items-center justify-between gap-3 border-t border-white/10 pt-3">
                    <p className="m-0 text-xs text-bzgray-500">
                      {portfolio
                        ? `Updated ${dayjs(portfolio.updatedAt).format('MMM D, YYYY')}`
                        : 'Holdings sync from Edge'}
                    </p>
                    <span className="inline-flex items-center gap-0.5 text-xs font-bold text-blue-500 transition-colors duration-150 group-hover:text-blue-400">
                      View portfolio
                      <TbArrowUpRight
                        className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        aria-hidden
                      />
                    </span>
                  </div>
                </a>
              );
            })
          )}
        </div>

        <div>
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <h3 className="m-0 text-sm font-bold uppercase tracking-[0.08em] text-slate-300">Latest trade ideas</h3>
            {!isLoading && displayedIdeas.length > 0 ? (
              <span className="text-xs font-semibold text-bzgray-500">{displayedIdeas.length} ideas</span>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-3">
            {isLoading ? (
              Array.from({ length: IDEA_DISPLAY_LIMIT }).map((_, index) => <EdgeIdeaCardSkeleton key={index} />)
            ) : displayedIdeas.length === 0 ? (
              <EdgeIdeasEmptyState />
            ) : (
              displayedIdeas.map(article => {
                const isStockOfTheDay = article.tags.some(tag => tag.tid === STOCK_OF_THE_DAY_TAG);

                return (
                  <a
                    key={article.id}
                    className="group flex min-h-[124px] flex-col justify-between gap-3 rounded-xl border border-bzgray-800 bg-[#192940] p-4 no-underline transition-[border-color,background-color] duration-150 ease-out hover:border-blue-500/40 hover:bg-[#1e2d43] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div
                      className="line-clamp-3 text-sm font-bold leading-snug text-balance text-white transition-colors duration-150 group-hover:text-blue-400"
                      dangerouslySetInnerHTML={{ __html: sanitizeHTML(article?.title ?? '') }}
                    />
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {isStockOfTheDay ? (
                          <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-300">
                            <BsFillTrophyFill size={10} />
                            Stock of the day
                          </span>
                        ) : null}
                        {article.tickers.slice(0, 3).map((ticker, index) => (
                          <span key={`${article.id}-${index}`} className="rounded-md bg-white/5 px-2 py-0.5 text-xs font-bold text-slate-200 ring-1 ring-white/10">
                            {ticker.name}
                          </span>
                        ))}
                        {article.tickers.length > 3 ? (
                          <span className="text-xs font-semibold text-bzgray-500">+{article.tickers.length - 3}</span>
                        ) : null}
                      </div>
                      <p className="m-0 text-xs text-bzgray-500">
                        {dayjs(article.updated).fromNow()}
                        {article.author ? ` · ${article.author}` : ''}
                      </p>
                    </div>
                  </a>
                );
              })
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EdgeSection;
